// Couleurs du club (D-024) : à partir d'UNE couleur choisie par le club, les
// variantes lisibles partout — en clair comme en sombre, en fond comme en texte.
// Fonctions pures (testées) ; l'application au document est dans ui/theme.ts.

export interface BrandPalette {
  light: { fill: string; ink: string; text: string };   // thème clair
  dark: { fill: string; ink: string; text: string };    // thème sombre
}

const HEX = /^#[0-9a-fA-F]{6}$/;
/** Couleurs proposées d'un geste (couleurs du club, page de création d'un club D-088). */
export const BRAND_PRESETS = ['#C41E3A', '#E8590C', '#FACC15', '#16A34A', '#0F766E', '#0EA5E9', '#1D4ED8', '#6D28D9', '#16181D'] as const;
export const isHexColor = (s: unknown): s is string => typeof s === 'string' && HEX.test(s);

const rgb = (hex: string): [number, number, number] =>
  [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
const hex = (c: [number, number, number]): string =>
  '#' + c.map(v => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('').toUpperCase();

/** Luminance relative (WCAG 2). */
export function luminance(color: string): number {
  const [r, g, b] = rgb(color).map(v => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Rapport de contraste WCAG entre deux couleurs. */
export function contrast(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p) as [number, number];
  return (x + 0.05) / (y + 0.05);
}

/** Mélange `color` vers `toward` (0 = color, 1 = toward). */
export function mix(color: string, toward: string, t: number): string {
  const a = rgb(color), b = rgb(toward);
  return hex([0, 1, 2].map(i => a[i]! + (b[i]! - a[i]!) * t) as [number, number, number]);
}

/** Pousse `color` vers `toward` jusqu'à atteindre le contraste voulu sur `ground`. */
function untilContrast(color: string, toward: string, ground: string, target: number): string {
  for (let t = 0; t <= 1.0001; t += 0.05) {
    const c = mix(color, toward, t);
    if (contrast(c, ground) >= target) return c;
  }
  return toward;
}

const WHITE = '#FFFFFF', INK = '#16181D', LIGHT_BG = '#FFFFFF', DARK_BG = '#1B1D20';

/** Encre (texte posé sur la couleur) : blanc ou quasi-noir, le plus lisible des deux. */
export const inkOn = (fill: string): string => (contrast(WHITE, fill) >= contrast(INK, fill) ? WHITE : INK);

/**
 * Palette complète. En clair, la couleur du club est gardée telle quelle pour
 * les fonds (bandeau, boutons) ; le TEXTE en couleur est assombri si besoin
 * (jaune, ciel…). En sombre, une couleur trop foncée (marine, bordeaux) est
 * éclaircie pour rester visible sur fond noir.
 */
export function palette(color: string): BrandPalette {
  const c = isHexColor(color) ? color.toUpperCase() : '#E8590C';
  const darkFill = untilContrast(c, WHITE, DARK_BG, 3);
  return {
    light: { fill: c, ink: inkOn(c), text: untilContrast(c, '#000000', LIGHT_BG, 4.5) },
    dark: { fill: darkFill, ink: inkOn(darkFill), text: untilContrast(c, WHITE, DARK_BG, 4.5) },
  };
}

/** Monogramme d'un club sans logo : sigle, sinon initiales du nom (3 lettres max). */
export function monogram(name: string, shortName?: string | null): string {
  const s = (shortName ?? '').trim();
  if (s) return s.slice(0, 4).toUpperCase();
  const words = name.replace(/[^\p{L}\p{N}\s'-]/gu, ' ').split(/[\s'-]+/).filter(w => w.length > 1);
  return (words.map(w => w[0]!).join('').slice(0, 3) || name.slice(0, 2)).toUpperCase();
}
